// The ONE action row of the full-screen flows (KAN-763; estimate builder now,
// invoice builder KAN-764 and unscheduled visit KAN-765 next). It owns the
// primary button's BOX so the primary never changes size or place across
// steps and beats:
//
//   phone / tablet — [left slot (crossfades by `leftKey`)] [primary slot, right]
//   desktop rail   — [summary; null folds it on the split's clock]
//                    [secondary col | primary slot]  (fr columns; the secondary
//                    column is 0fr until `secondary` is given, then the primary
//                    SPLITS into two buttons)
//
// The primary slot (FullScreenActionParts.FullScreenPrimarySlot) hosts the idle
// button or the save/send beat (ActionBeatFill) in the SAME 44px box; on touch
// its width is the widest of the idle labels, never the live/working label.
// Motion is timer-based; reduced motion swaps instantly (layout identical).
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { foundation } from '../../../ui';
import { BEAT_EASE } from '../../../ui/actionBeatMotion';
import useReducedMotion from '../../../hooks/ai/useReducedMotion';
import { stripIdentity } from './useLeavingSwap';
import {
  CrossfadeCell, FullScreenQuietButton, FullScreenPrimarySlot,
  FULLSCREEN_PRIMARY_SLOT_HEIGHT, FULLSCREEN_BAR_ROW_MIN, FULLSCREEN_ACTION_SPLIT_MS,
} from './FullScreenActionParts';

const { spacePx } = foundation;

/** Public constants for flows that need to align with the slot. */
export const FULLSCREEN_PRIMARY_SLOT = { height: FULLSCREEN_PRIMARY_SLOT_HEIGHT };
export { FULLSCREEN_BAR_ROW_MIN, FULLSCREEN_ACTION_SPLIT_MS, FullScreenQuietButton };

// No row gap: the left cell owns its trailing margin so the gap collapses with it.
const touchRow = { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' };
const leftCell = { minWidth: 0, alignItems: 'center' };

/**
 * @param {object} props
 * @param {'phone'|'tablet'|'desktop'} props.layout
 * @param {import('react').ReactNode} [props.left] - phone/tablet left slot contents (total, hint, quiet action).
 * @param {string|number} [props.leftKey] - changes when `left` should crossfade.
 * @param {{label: string, labels?: string[], onClick: () => void, testId?: string, busy?: boolean, busyLabel?: string, disabled?: boolean}} props.primary
 *   `labels` = every idle label the slot must fit across steps (the slot's width is the widest).
 * @param {{label: string, onClick: () => void, testId?: string}|null} [props.secondary] - desktop only: splits the primary into two columns.
 * @param {{phase: 'working'|'success', workingLabel: string, successLabel: string, reduced?: boolean, tone?: string, sequentialLabels?: boolean}|null} [props.beat]
 *   `sequentialLabels` (opt-in, KAN-772): working -> success never shows two legible labels at once.
 * @param {import('react').ReactNode} [props.summary] - desktop block above the row; null folds it away on the split's clock.
 * @param {boolean} [props.foldSecondaryOnBeat] - desktop opt-in (KAN-772): while a beat runs the split COLLAPSES
 *   (secondary column folds to 0fr, the primary grows to the full row) on the split's clock, and re-splits when the
 *   beat ends. Default false: the columns stay 1fr/1fr through a beat (the secondary only fades).
 * @param {number} [props.minRowHeight] - touch row-height floor override (default: the layout's stacked-total floor). Flows whose footer must match a sibling family's pass 44.
 * @param {boolean} [props.fillPrimary] - phone/tablet: nothing in the left slot, so the primary is FULL WIDTH at rest and through the beat (no left cell is rendered).
 * @param {string} [props.testId]
 */
export default function FullScreenActionRow({
  layout, left = null, leftKey = 'left', primary, secondary = null, beat = null, summary = null,
  foldSecondaryOnBeat = false, minRowHeight = undefined, fillPrimary = false, testId = 'fullscreen-action-row',
}) {
  const reduced = useReducedMotion();
  const touch = layout !== 'desktop';
  // Keep the last summary rendered (as an id-less, inert copy) while it folds
  // away (desktop step 2 -> 3); a timer removes it, never transitionend.
  const prevSummaryRef = useRef(summary);
  const [foldingSummary, setFoldingSummary] = useState(null);
  useLayoutEffect(() => {
    if (summary) setFoldingSummary(null);
    else if (prevSummaryRef.current && !reduced) setFoldingSummary(prevSummaryRef.current);
    prevSummaryRef.current = summary;
  }, [summary, reduced]);
  useEffect(() => {
    if (!foldingSummary) return undefined;
    const t = setTimeout(() => setFoldingSummary(null), FULLSCREEN_ACTION_SPLIT_MS + 80);
    return () => clearTimeout(t);
  }, [foldingSummary]);

  // The left cell's width at rest, so its content keeps that width while the cell
  // collapses: the hint is clipped, never re-wrapped into a taller column (a 2-line
  // hint grew the bar ~120px on a phone during the setup guide's "Saving" beat).
  const leftRef = useRef(null);
  const leftIdleWidthRef = useRef(null);
  useLayoutEffect(() => {
    if (!beat && leftRef.current) leftIdleWidthRef.current = leftRef.current.getBoundingClientRect().width;
  });
  const lockedLeftWidth = beat ? leftIdleWidthRef.current : null;

  if (touch) {
    return (
      <div style={{ ...touchRow, minHeight: minRowHeight ?? (FULLSCREEN_BAR_ROW_MIN[layout] || FULLSCREEN_BAR_ROW_MIN.tablet) }} data-testid={testId} data-layout={layout}>
        {/* During a beat the left cell collapses (width, gap and opacity on the
            slot's clock) and the primary slot grows to the full row; it returns
            when the beat ends. Inert while collapsed. `fillPrimary`: no left cell at all. */}
        {fillPrimary ? null : (
        <div
          ref={leftRef}
          style={{
            flexGrow: beat ? 0 : 1, flexShrink: 1, flexBasis: '0%', minWidth: 0, overflow: 'hidden',
            marginRight: beat ? 0 : spacePx[3], opacity: beat ? 0 : 1,
            transition: reduced ? 'none'
              : `flex-grow ${FULLSCREEN_ACTION_SPLIT_MS}ms ${BEAT_EASE.out}, margin-right ${FULLSCREEN_ACTION_SPLIT_MS}ms ${BEAT_EASE.out}, opacity ${FULLSCREEN_ACTION_SPLIT_MS}ms ${BEAT_EASE.out}`,
          }}
          data-testid="fullscreen-action-left"
          data-state={beat ? 'collapsed' : 'open'}
          inert={beat ? true : undefined}
          aria-hidden={beat ? true : undefined}
        >
          <CrossfadeCell swapKey={leftKey} reduced={reduced} style={lockedLeftWidth ? { ...leftCell, width: lockedLeftWidth } : leftCell}>{left}</CrossfadeCell>
        </div>
        )}
        <FullScreenPrimarySlot primary={primary} beat={beat} touch reduced={reduced} fill={fillPrimary} />
      </div>
    );
  }

  const split = Boolean(secondary) && !(foldSecondaryOnBeat && beat);
  const tween = reduced ? 'none'
    : `grid-template-columns ${FULLSCREEN_ACTION_SPLIT_MS}ms ${BEAT_EASE.out}, gap ${FULLSCREEN_ACTION_SPLIT_MS}ms ${BEAT_EASE.out}`;
  const secondaryShown = split && !beat;
  return (
    <div data-testid={testId} data-layout={layout}>
      {/* The summary folds on the SAME clock as the button split (grid-rows
          0fr tween, last summary kept while it folds) so the two motions are
          one beat, not one after the other. */}
      <div
        style={{
          display: 'grid',
          gridTemplateRows: summary ? '1fr' : '0fr',
          opacity: summary ? 1 : 0,
          transition: reduced ? 'none'
            : `grid-template-rows ${FULLSCREEN_ACTION_SPLIT_MS}ms ${BEAT_EASE.out}, opacity 200ms ease`,
        }}
        data-testid="fullscreen-action-summary"
        data-state={summary ? 'open' : 'folded'}
        aria-hidden={summary ? undefined : true}
        inert={!summary}
      >
        <div style={{ minHeight: 0, overflow: 'hidden' }}>
          {summary ? <div style={{ paddingBottom: spacePx[3] }}>{summary}</div> : null}
          {!summary && foldingSummary ? (
            <div style={{ paddingBottom: spacePx[3] }} ref={stripIdentity} data-leaving>{foldingSummary}</div>
          ) : null}
        </div>
      </div>
      <div
        style={{ display: 'grid', gridTemplateColumns: split ? '1fr 1fr' : '0fr 1fr', gap: split ? spacePx[3] : 0, transition: tween }}
        data-testid="fullscreen-action-columns"
        data-split={split ? 'true' : 'false'}
      >
        <div
          style={{ minWidth: 0, overflow: 'hidden', opacity: secondaryShown ? 1 : 0, transition: reduced ? 'none' : 'opacity 200ms ease' }}
          inert={!secondaryShown}
          aria-hidden={!secondaryShown || undefined}
          data-testid="fullscreen-action-secondary-col"
        >
          {secondary ? <FullScreenQuietButton block label={secondary.label} onClick={secondary.onClick} testId={secondary.testId} hidden={!secondaryShown} /> : null}
        </div>
        <FullScreenPrimarySlot primary={primary} beat={beat} touch={false} reduced={reduced} />
      </div>
    </div>
  );
}
